<template>
  <form
    class="auto-form"
    @submit.prevent
  >
    <template v-if="fieldsInColumns.length">
      <div
        v-for="(fields, index) of fieldsInColumns"
        :key="index"
        class="auto-form-row"
      >
        <template v-for="field in fields">
          <!--
            We cannot use `WithSideDescription HoC (which is almost the same) for a couple of reasons:
            - It accepts component ref, which we cannot have because of that `<component :is="field.type">`
            which sometimes is an actual component ref, but most of the time it's a string like 'FormText'.
            - It hijacks `description=''`, which we want to preserve for fields.
            - We need different styling for the internal v-html.
            So all in all it's easier to have it done like that, but once we figure out
            how to make all `field.type` a component ref, then we can start using HoC for this.
          -->
          <WithDescriptionLong
            v-if="field.descriptionLong"
            :key="field.dataKey || field.name"
            :descriptionLong="field.descriptionLong"
          >
            <component
              :is="field.type"
              class="auto-form-column"
              :invalid="isFieldInvalid(field.dataKey || field.name)"
              :dirty="isFieldDirty(field.dataKey || field.name)"
              :error="getFieldErrorMessage(field.dataKey || field.name, field)"
              :disabled="disabled || field.disabled"
              :read-only="readOnly"
              :data-test="field.name"
              :property="field.dataKey || field.name"
              v-bind="{ validation, ...field }"
              :value="designateValue(field)"
              :parentValue="value"
              :formValue="designateValue(field)"
              :parent="value"
              :nestedMode="nestedMode"
              v-on="{
                ...field.listeners,
                input: $event => update(field.dataKey || field.name, $event),
                update: $event => updateByPath(field.dataKey || field.name, $event),
                blur: () => onBlur(field.dataKey || field.name),
              }"
              @validation="onFieldSelfValidation(field.dataKey || field.name, $event)"
            />
          </WithDescriptionLong>
          <component
            :is="field.type"
            v-else
            :key="field.dataKey || field.name"
            class="auto-form-column"
            :invalid="isFieldInvalid(field.dataKey || field.name)"
            :dirty="isFieldDirty(field.dataKey || field.name)"
            :error="getFieldErrorMessage(field.dataKey || field.name, field)"
            :disabled="disabled || field.disabled"
            :read-only="readOnly"
            :data-test="field.name"
            :property="field.dataKey || field.name"
            v-bind="{ validation, ...field }"
            :value="designateValue(field)"
            :parentValue="value"
            :parent="value"
            :nestedMode="nestedMode"
            v-on="{
              ...field.listeners,
              input: $event => update(field.dataKey || field.name, $event),
              update: $event => updateByPath(field.dataKey || field.name, $event),
              blur: () => onBlur(field.dataKey || field.name),
            }"
            @validation="onFieldSelfValidation(field.dataKey || field.name, $event)"
          />
        </template>
      </div>
    </template>
    <div
      v-if="layout.length"
      class="d-flex flex-column"
    >
      <component
        :is="child.component"
        v-for="(child, index) of layout"
        :key="index"
        v-bind="{ validation, dirtyOn, ...child.props }"
        v-on="{
          ...child.listeners,
          change: childrenChange,
          update: $event => updateByPath(property, $event),
          blur: onBlur,
        }"
        @validation="onNestedSchemaValidationUpdate($event)"
      />
    </div>
  </form>
</template>

<script>
import _debounce from 'lodash/debounce'
import _toPairs from 'lodash/toPairs'
import _flatten from 'lodash/flatten'
import { computed, toRefs, ref, watch } from '@vue/composition-api'
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import * as oldValidators from 'vuelidate/lib/validators'
import { getValidators, getValidationWithoutAggregatedValues, combineValidations } from '@/utils/vuelidate'
import { configToValidation, configToErrorMessages } from '@/utils/validationConfigConverter'
import { DEFAULT_ERROR_MESSAGES } from '@/constants'
import { generateToken } from '@shared/utils/common'
import FormCheckbox from './FormCheckbox'
import FormText from './FormText'
import FormUrl from './FormUrl'
import FormNumber from './FormNumber'
import FormMoney from './FormMoney'
import FormDate from './FormDate'
import FormTime from './FormTime'
import FormRange from './FormRange'
import FormTextarea from './FormTextarea'
import FormSelect from './FormSelect'
import FormFile from './FormFile'
import FormYesNo from './FormYesNo'
import FormCourseNumber from './FormCourseNumber'
import FormCourseAliases from './FormCourseAliases'
import FormCourseSelect from './FormCourseSelect'
// eslint-disable-next-line no-restricted-imports
import FormProgramSelect from '@cm/client/components/Forms/FormProgramSelect.vue'
import FormDepartmentSelect from './FormDepartmentSelect'
import FormDepartmentOwnership from './FormDepartmentOwnership'
import FormCatalogSelect from './FormCatalogSelect'
import FormComponentSelect from './FormComponentSelect'
import FormOrganizationSelect from './specific/OrganizationSelect'
import FormUserSelect from './FormUserSelect'
import FormTermSelect from './FormTermSelect'
import FormLearningOutcomes from './FormLearningOutcomes.vue'
import FormCredits from './FormCredits.vue'
import FormTopics from './FormTopics.vue'
import FormComponents from './FormComponents.vue'
import FormRequisites from './FormRequisites.vue'
import FormUploadImage from './FormUploadImage'
import FormSectionSelect from './FormSectionSelect'
import FormAttributeSelect from './FormAttributeSelect'
import FormCampusSelect from './FormCampusSelect'
import FormGradeModeSelect from './FormGradeModeSelect'
import FormGradeModesMultiSelect from './FormGradeModesMultiSelect'
import FormLocationSelect from './FormLocationSelect'
import FormFieldOfStudySelect from './FormFieldOfStudySelect'
import FormDegreeSelect from './FormDegreeSelect'
import FormCipCodeSelect from './FormCipCodeSelect'
import FormHegisCodeSelect from './FormHegisCodeSelect'
import FormCollegeSelect from './FormCollegeSelect'
import FormCareerSelect from './FormCareerSelect'
import FormProgramLevelSelect from './FormProgramLevelSelect'
import FormRequirementDesignationSelect from './FormRequirementDesignationSelect'
import FormMilestoneSelect from './FormMilestoneSelect'
import FormRequirementGroupSelect from './FormRequirementGroupSelect'
import FormWysiwyg from './FormWysiwyg'
import FormSlider from './FormSlider'
import FormOwnersSelect from './FormOwnersSelect'
import FormObjectMergeSettings from './FormObjectMergeSettings'
import FormProgramChanges from './FormProgramChanges.vue'

import matchInputTypes from './matchInputTypes'

function deepMatch (field) {
  if (Array.isArray(field)) {
    return field.map(deepMatch)
  } else {
    return {
      ...field,
      ...matchInputTypes(field),
    }
  }
}

function fieldsToArray (fields) {
  return Object.keys(fields).map(fieldKey => ({
    ...fields[fieldKey],
    dataKey: fieldKey,
    name: fieldKey,
  }))
}

function calculateRules (fields) {
  return fields.filter(field => field.required || (field.validations && Object.keys(field.validations).length) || (field.config?.validations && Object.keys(field.config.validations).length))
    .reduce((acc, field) => {
      const validators = {
        ...(field.required && {
          required: {
            $message: `${field.label} is required`,
            $validator: required.$validator,
          },
          $autoDirty: true,
        }),
        ...(field.config?.validations && {
          // TODO: See if we can maybe use `useFieldRules` from `useFieldValidations`
          ...Object
            .entries(configToValidation(field))
            .reduce((validations, [ validatorName, validator ]) => ({
              ...validations,
              [validatorName]: {
                $message: configToErrorMessages(field)[validatorName],
                $validator: validator,
              },
            }), {}),
          $autoDirty: true,
        }),
      }
      const hasValidators = Object.keys(validators).length
      return {
        ...acc,
        ...hasValidators && { [field.dataKey || field.name]: validators },
      }
    }, {})
}

export default {
  name: 'SchemaForm',
  inheritAttrs: false,
  components: {
    FormCheckbox,
    FormWysiwyg,
    FormText,
    FormUrl,
    FormDate,
    FormTime,
    FormRange,
    FormSelect,
    FormFile,
    FormTextarea,
    FormYesNo,
    FormCourseSelect,
    FormCourseNumber,
    FormCourseAliases,
    FormProgramSelect,
    FormDepartmentSelect,
    FormDepartmentOwnership,
    FormCatalogSelect,
    FormComponentSelect,
    FormOrganizationSelect,
    FormUserSelect,
    FormTermSelect,
    FormLearningOutcomes,
    FormCredits,
    FormTopics,
    FormComponents,
    FormRequisites,
    FormNumber,
    FormMoney,
    FormSlider,
    FormUploadImage,
    FormSectionSelect,
    FormAttributeSelect,
    FormFieldOfStudySelect,
    FormDegreeSelect,
    FormRequirementGroupSelect,
    FormCampusSelect,
    FormLocationSelect,
    FormGradeModeSelect,
    FormGradeModesMultiSelect,
    FormCipCodeSelect,
    FormHegisCodeSelect,
    FormCollegeSelect,
    FormCareerSelect,
    FormProgramLevelSelect,
    FormRequirementDesignationSelect,
    FormMilestoneSelect,
    FormOwnersSelect,
    FormObjectMergeSettings,
    FormProgramChanges,
    WithDescriptionLong: () => import(/* webpackChunkName: 'WithDescriptionLong' */ '@/components/Forms/WithDescriptionLong'),
  },
  props: {
    // This is a nasty HACK, to make Meetings validation work (em)
    // Onve we have this issue sorted out we can get rid of this functionality:
    // https://www.notion.so/coursedog1/Adjust-validation-system-to-handle-arrays-validation-a5c546cd1c2f44d5989d21fae4f99927
    watchNestedHack: {
      type: Boolean,
      default: false,
    },
    fields: {
      type: [ Object, Array ],
      default: () => [],
    },
    value: {
      type: Object,
      default: () => ({}),
    },
    columns: {
      type: [ Number, String ],
      default: 'auto',
      validator (columns) {
        return typeof columns === 'number' || columns === 'auto'
      },
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    layout: {
      type: Array,
      default: () => [],
    },
    validation: {
      type: Boolean,
      default: false,
    },
    property: {
      type: String,
      default: '',
    },
    dirtyOn: {
      type: String,
      default: 'manual',
      validator: function (value) {
        return [
          'blur',
          'input',
          'manual',
        ].indexOf(value) !== -1
      },
    },
    newVuelidate: {
      type: Boolean,
      default: false,
    },
    nestedMode: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    // We need id to track validation errors when dealing with lists
    id: generateToken(10),
    // Local store that keeps all validation stuff that comes from child fields and nested forms
    nestedValidationObjects: {},
  }),
  setup (props) {
    const formValue = computed(() => props.value)
    const { validation, fields, layout, newVuelidate } = toRefs(props)

    const isLayoutContainer = !!layout.value?.length
    const normalizedFields = computed(() => Array.isArray(fields.value)
      ? fields.value.map(deepMatch)
      : fieldsToArray(fields.value).map(deepMatch))
    const flatFields = computed(() => _flatten(normalizedFields.value))

    // NOTE: We cannot have validationRules as a computed,
    // becuase for some reason it causes infinte loop and freezes browser.
    // Not sure what's the reason, but might be some unhandled case
    // in new Vuelidate - as it only seems to appear when using
    // SchemaForm (newVuelidate=true) in a Modal (e.g EM Rooms +New Room).
    const validationRules = ref(calculateRules(flatFields.value))
    function updateRules () {
      validationRules.value = calculateRules(flatFields.value)
    }
    const debouncedUpdateRules = _debounce(updateRules, 3000)
    watch(flatFields, debouncedUpdateRules)

    const shouldValidate = !isLayoutContainer && !!validation.value

    const $v = shouldValidate
      ? useVuelidate(validationRules, formValue)
      : null

    return {
      normalizedFields,
      flatFields,
      isLayoutContainer,
      ...newVuelidate.value && { $v },
    }
  },
  computed: {
    validationObject () {
      return combineValidations({
        ...getValidationWithoutAggregatedValues(this.$v?.value || {}),
        ...this.nestedValidationObjects,
      })
    },
    invalidValidatorsNumber () {
      return Object.values(getValidationWithoutAggregatedValues(this.validationObject))
        .filter(field => field.$invalid)
        .length
    },
    fieldsInColumns () {
      const fields = this.normalizedFields
      if (this.columns === 'auto' || this.columns === 1) {
        return this.normalizedFields.map(
          fields => Array.isArray(fields) ? fields : [ fields ]
        )
      } else {
        const rowsLength = Math.ceil(fields.length / this.columns)
        return new Array(rowsLength)
          .fill()
          .map((el, index) => fields.slice(
            index * this.columns,
            index * this.columns + this.columns
          ))
      }
    },
  },
  watch: {
    nestedValidationObjects () {
      if (this.watchNestedHack) {
        this.onLocalValidationChange()
      }
    },
    'validationObject.$anyDirty' () {
      if (!this.newVuelidate) {
        this.onLocalValidationChange()
      }
    },
    '$v.$anyDirty' () {
      if (this.newVuelidate) {
        this.onLocalValidationChange()
      }
    },
    // Since watching on whole $v object is very inefficient,
    // it's easier to just watch on number of errors
    'invalidValidatorsNumber': {
      // We need to emit validation object immediately to let parent schema form know
      // in case if one of its children is invalid from the beginning
      immediate: true,
      handler () {
        if (!this.newVuelidate) {
          this.onLocalValidationChange()
        }
      },
    },
    '$v.$invalid': {
      // We need to emit validation object immediately to let parent schema form know
      // in case if one of its children is invalid from the beginning
      immediate: true,
      handler () {
        if (this.newVuelidate) {
          this.onLocalValidationChange()
        }
      },
    },
  },
  // TODO: Think about some prop like `:validationOnDestroy="false"`, to not emit event on destroy in some case,
  // as it throws error when listening in parent like e.g `@validation="$v => $v.$touch()"
  beforeDestroy () {
    // In some cases (e.g validating SchemaForms array) we have to inform parent that
    // it's internal validation shouldn't be used anymore
    this.$emit('validation', {}, this.id)
  },
  methods: {
    touchField (property) {
      if (this.$v && this.$v.value && this.$v.value[property]) this.$v.value[property].$touch()
    },
    onBlur (property) {
      if (this.$v && this.newVuelidate) {
        return this.$v.$touch()
      }
      if (this.validation === true && this.dirtyOn === 'blur') {
        return this.touchField(property)
      }
    },
    updateByPath (prop, { property, value }) {
      const mergedPath = prop
        ? `${prop}.${property}`
        : property
      this.$emit('update', {
        property: mergedPath,
        value,
      })
    },
    update (property, value) {
      if (this.validation === true && this.dirtyOn === 'input' && !this.newVuelidate) {
        this.touchField(property)
      }
      this.$emit('input', {
        ...this.value,
        [property]: value,
      })
      this.$emit('change', { property, value })
      this.$emit('update', { property, value })
    },
    childrenChange ({ property, value }) {
      this.update(property, value)
    },
    isFieldInvalid (dataKey) {
      if (this.newVuelidate && this.$v?.[dataKey]) {
        return this.$v[dataKey].$invalid
      }
      return this.validation && !this.isLayoutContainer && this.$v && this.$v.value && this.$v.value[dataKey]
        ? this.$v.value[dataKey].$invalid
        : false
    },
    isFieldDirty (dataKey) {
      if (this.newVuelidate && this.$v?.[dataKey]) {
        return this.$v[dataKey].$anyDirty
      }
      return this.validation && !this.isLayoutContainer && this.$v && this.$v.value && this.$v.value[dataKey]
        ? this.$v.value[dataKey].$dirty
        : false
    },
    // Detect why field is invalid
    getInvalidValidators (property) {
      const validationPairs = _toPairs(getValidators(this.$v.value[property]))
      return validationPairs.filter(([ validator, isValid ]) => !isValid).map(([ validator ]) => validator)
    },
    // Prepare field error message
    getFieldErrorMessage (property, field) {
      if (!this.isFieldInvalid(property)) return ''
      if (this.newVuelidate) {
        return this.$v[property].$errors.map(e => e.$message).join(', ')
      }
      const flatField = this.flatFields.find(f => f.dataKey === property || f.name === property)
      // TODO: This case probably won't ever occur,
      // as previously errorMessages were calculated sometimes in parent
      // as `errorMessages: configToErrorMessages(question)`,
      // but we do it only here from now on.
      if (flatField.errorMessages) {
        return flatField.errorMessages[this.getInvalidValidators(property)[0]]
      }
      const errorMessages = configToErrorMessages(flatField)
      return errorMessages?.[this.getInvalidValidators(property)[0]] ||
        DEFAULT_ERROR_MESSAGES[this.getInvalidValidators(property)[0]]() ||
        DEFAULT_ERROR_MESSAGES._()
    },
    // If field emits its own validation object it should be catched and combined with local validation
    onFieldSelfValidation (property, $v) {
      if (!this.validation) return
      this.nestedValidationObjects = {
        ...this.nestedValidationObjects,
        [property]: $v,
      }
    },
    // If nested form emits its own validation object it should be catched and combined with local validation
    // but without aggregated values (eg. $anyError) since they will be invalid after merge
    onNestedSchemaValidationUpdate ($v) {
      this.nestedValidationObjects = {
        ...this.nestedValidationObjects,
        ...getValidationWithoutAggregatedValues($v),
      }
    },
    // Emit local validation object to parent schema form (if any) or to be utilized in parent component
    onLocalValidationChange () {
      if (this.validation === true) {
        if (this.$v && this.newVuelidate) {
          return this.$emit('validation', this.$v, this.id)
        }
        return this.$emit('validation', this.validationObject, this.id)
      }
    },
    designateValue (field) {
      return this.value.hasOwnProperty(field.dataKey)
        ? this.value[field.dataKey]
        : this.value[field.name]
    },
  },
  validations () {
    if (this.newVuelidate) return undefined
    // If schema form is rather layout container than actual form and it has nested schemaforms
    // it should combine validation objects emitted by its children schemaforms so self validation is
    // disabled. See: validationObject
    if (this.isLayoutContainer || !this.validation) return {}
    // Schema form with fields should validate its fields based on typical attributes (eg. required)
    // and custom validations passed with schema

    return {
      value: this.flatFields.reduce((acc, field) => ({
        ...acc,
        [field.dataKey || field.name]: {
          ...(field.required ? { required: oldValidators.required } : {}),
          ...(field.config?.validations && configToValidation(field)),
        },
      }), {}),
    }
  },
}
</script>

<style scoped>
.auto-form-column {
  flex-grow: 12;
  width: 100%;
  flex-basis: 150px;
  padding: 0 0.5rem;
}

.auto-form-row {
  display: flex;
  flex-flow: row wrap;
  margin: 0 -0.5rem;
  align-items: end;
  margin-bottom: 0.5rem;
}

.auto-form {
  width: 100%;
  margin-bottom: 0px;
}

.auto-form >>> label {
  display: flex;
  align-items: center;
}
</style>
